import React, { useState } from 'react'

import {
  Box,
  Button as DeprecatedButton,
  Popover,
  Text,
} from '@factorialco/deprecated-design-system'
import { F0Button } from '@factorialco/f0-react'
import { Warning } from '@factorialco/f0-react/icons/app'
import { CompensationValidationFragment } from 'generated/resources/graphql'

import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import useDeleteCompensationsEmployeesCompensationsValidation from 'modules/compensations/hooks/useDeleteCompensationsEmployeesCompensationsValidation'

interface CompensationsAlertPopoverProps {
  readonly?: boolean
  shouldShowWarning: boolean
  validations?: CompensationValidationFragment[]
}

export function CompensationsAlertPopover({
  readonly,
  shouldShowWarning,
  validations,
}: CompensationsAlertPopoverProps) {
  const [isWarningPopoverOpen, setIsWarningPopoverOpen] = useState(false)
  const { deleteValidation, loading } = useDeleteCompensationsEmployeesCompensationsValidation()

  if (!shouldShowWarning) {
    return null
  }

  const aboveLimitValidation = validations?.find(
    (validation) => validation.validationType === 'above_limit'
  )
  const pendingToDefineValidation = validations?.find(
    (validation) => validation.validationType === 'pending_to_define'
  )

  const isAboveLimitAlert = !!aboveLimitValidation
  const isPendingToDefineAlert = !!pendingToDefineValidation

  const handleDeleteValidation = async (validationId: string) => {
    const result = await deleteValidation({
      id: validationId,
    })

    const handled = handleResult(result, (_errors) => {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('common.error'),
      })
    })

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
      })
      setIsWarningPopoverOpen(false)
    }
  }

  const getWarningTitle = () => {
    if (isAboveLimitAlert) {
      return i18n.t(
        'compensations.PayrollRunCompensationsList.alertsSidePanelButton.aboveLimit.title'
      )
    }

    if (isPendingToDefineAlert) {
      return i18n.t(
        'compensations.PayrollRunCompensationsList.alertsSidePanelButton.variablePending.title'
      )
    }
  }

  const warningTitle = getWarningTitle()

  const warningDescription = i18n.t(
    'compensations.PayrollRunCompensationsList.alertsSidePanelButton.aboveLimit.description'
  )

  const warningButtonText = i18n.t(
    'compensations.PayrollRunCompensationsList.alertsSidePanelButton.aboveLimit.button'
  )

  return (
    <Box gap="s4">
      <Popover.ItemList
        attachment="top right"
        targetAttachment="bottom center"
        toggle={() => () => setIsWarningPopoverOpen(!isWarningPopoverOpen)}
        open={isWarningPopoverOpen}
        gapDivider={false}
      >
        <F0Button
          variant="outline"
          icon={Warning}
          hideLabel
          label="Warning"
          type="button"
          disabled={readonly}
        />
        <Box gap="s8">
          <Box gap="s4" alignItems="flexStart">
            <Text.Header3 color="white">{warningTitle}</Text.Header3>
            {isAboveLimitAlert && (
              <Text.Normal color="white" size="100">
                {warningDescription}
              </Text.Normal>
            )}
          </Box>
          {isAboveLimitAlert && aboveLimitValidation && (
            <Box alignItems="center" width="full" paddingTop="s6">
              <DeprecatedButton
                onClick={() => handleDeleteValidation(aboveLimitValidation.id)}
                type="secondary-dark"
                disabled={loading || readonly}
              >
                {warningButtonText}
              </DeprecatedButton>
            </Box>
          )}
        </Box>
      </Popover.ItemList>
    </Box>
  )
}
